import React, { FC, memo } from 'react'
import { Box } from 'transfix-ui/core'
import { LightningFilledIcon } from 'transfix-icons'
import { Tag, TAG_STYLE, ITagProps, TAG_SIZE } from '../Tag'
import { AppointmentTimeRange } from './components/AppointmentTimeRange'
import { TAppointmentStatus, IDateRangePoint } from './types'

export interface IAppointmentTagProps
  extends Omit<ITagProps, 'tagStyle' | 'label' | 'outlineOnly'> {
  /**
   * @example 'confirmed'
   */
  status: TAppointmentStatus
  /**
   * @example { datetime: '2021-08-22T00:00:00.000Z', timezone: 'America/New_York' }
   */
  start: IDateRangePoint
  /**
   * @example { datetime: '2021-08-22T00:00:00.000Z', timezone: 'America/New_York' }
   */
  end?: IDateRangePoint
}

export const TAG_STYLES_BY_STATUS: Record<
  TAppointmentStatus,
  FC<Omit<IAppointmentTagProps, 'status'>>
> = {
  /**
   * 'TBD'
   * or 'Mon 08/22 TBD – Wed 08/24 TBD'
   */
  'no-appointment': ({ ...props }) => {
    const { end, start } = props
    return (
      <Tag
        tagStyle={TAG_STYLE.warning}
        label={start?.datetime ? <AppointmentTimeRange start={start} end={end} /> : 'TBD'}
        outlineOnly
        {...props}
      />
    )
  },
  /**
   * '00:01 - 23:59 EST'
   */
  unconfirmed: ({ end, start, ...rest }) => (
    <Tag
      tagStyle={TAG_STYLE.warning}
      label={<AppointmentTimeRange start={start} end={end} />}
      {...rest}
    />
  ),
  /**
   * '00:01 - 23:59 EST'
   */
  confirmed: ({ end, start, ...rest }) => (
    <Tag
      tagStyle={TAG_STYLE.spruceGreen}
      outlineOnly
      label={<AppointmentTimeRange start={start} end={end} />}
      {...rest}
    />
  ),
  /**
   * '08:00-16:00 EST - Work-in Needed'
   */
  'work-in-request-open': ({ end, start, ...rest }) => (
    <Tag
      tagStyle={TAG_STYLE.error}
      label={
        <Box component='span'>
          <AppointmentTimeRange start={start} end={end} /> - Work-in needed
        </Box>
      }
      {...rest}
    />
  ),
  /**
   * '08:00-16:00 EST - Reschedule Needed'
   */
  'reschedule-requested-open': ({ end, start, ...rest }) => (
    <Tag
      tagStyle={TAG_STYLE.error}
      label={
        <Box component='span'>
          <AppointmentTimeRange start={start} end={end} /> - Reschedule Needed
        </Box>
      }
      {...rest}
    />
  ),
  /**
   * '05:30 - 12:00 MST'
   */
  predicted: ({ end, start, ...rest }) => (
    <Tag
      tagStyle={TAG_STYLE.warning}
      outlineOnly
      label={<AppointmentTimeRange start={start} end={end} />}
      icon={<LightningFilledIcon size='small' />}
      {...rest}
    />
  ),
}

/**
 * Tag with value based on appointment information
 * @example
 * ```
 * <AppointmentTag
 *  status='confirmed'
 *  start={{
 *    datetime: '2021-08-22T00:00:00.000Z',
 *    timezone: 'America/New_York'
 *  }}
 *  end={{
 *    datetime: '2021-08-23T00:00:00.000Z',
 *    timezone: 'America/New_York'
 *  }}
 * />
 * ```
 */
export const AppointmentTag = memo<IAppointmentTagProps>(({ status, ...rest }) => {
  const TagComponent = TAG_STYLES_BY_STATUS[status]
  return <TagComponent size={TAG_SIZE.xsmall} {...rest} />
})

export default AppointmentTag
